import { Component, Input, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { Router } from "@angular/router";
import { BehaviorSubject } from "rxjs";
import { AuthService } from "../../../auth.service";
import { SingleChoice } from "../../../model/singlechoice";
import { RecurrenceFuture, RecurrenceImpact } from "../../../model/recurrence";
import { RecurrenceService } from "../../../services/recurrence.service";
import { NavigationHelper } from "../../../services/navigation.helper";

/**
 * Asks what should happen to the tickets still ahead before a series is ended.
 *
 * The panel version of what used to be a dialog. Like its sibling for a changed
 * rule it lives off the url alone - the ticket id read from the url, the impact
 * fetched here - so it survives the edit screen being destroyed on a phone, and
 * hands its answer back as a query parameter.
 *
 * 'Laat ze staan' is preselected: it is the choice that throws nothing away.
 */
@Component({
    selector: "recurrence-cancel-panel",
    standalone: true,
    imports: [CommonModule, FormsModule, TranslateModule],
    templateUrl: "./recurrence-cancel-panel.component.html",
    styleUrls: ["../recurrence-change-panel/recurrence-change-panel.component.css"],
})
export class RecurrenceCancelPanelComponent implements OnInit {
    @Input() choiceEvent: SingleChoice = SingleChoice.EMPTY;

    impact = new BehaviorSubject<RecurrenceImpact | null>(null);
    loading = true;

    future: RecurrenceFuture = "keep";

    /**
     * How long the series is, for the "occurrence 2 of 20" on each row.
     *
     * It comes from the series as it stands, not from the rule being proposed:
     * the rows are tickets that already exist, and their place in the series is
     * the one they already have. A series with no end has no total to show, so
     * the row says the occurrence and stops there.
     */
    seriesTotal = 0;
    seriesUnbounded = true;

    private ticketId = 0;

    constructor(
        private router: Router,
        private nav: NavigationHelper,
        private auth: AuthService,
        private recurrences: RecurrenceService
    ) {}

    ngOnInit(): void {
        // The id comes from the navigation helper, not from ActivatedRoute.
        // This app has a single router-outlet, at the very top; everything
        // below the main screen is placed by *ngIf in the frame templates, so
        // no component down here is a routed component and none of them ever
        // sees a route parameter. The url is read by hand instead.
        this.ticketId = Number(this.nav.freshNavigation.id);

        if (!this.ticketId) {
            this.leave(null);
            return;
        }
        this.recurrences
            .byTicket(this.auth.token, this.ticketId)
            .subscribe(() => {
                const view = this.recurrences.currentRecurrence.value;

                this.seriesTotal = view ? view.total : 0;
                this.seriesUnbounded = !view || view.unbounded;

                this.recurrences
                    .impact(this.auth.token, this.ticketId)
                    .subscribe(() => {
                        this.impact.next(this.recurrences.currentImpact.value);
                        this.loading = false;
                    });
            });
    }

    get count(): number {
        return this.impact.value ? this.impact.value.future.length : 0;
    }

    get hasFuture(): boolean {
        return this.count > 0;
    }

    confirm(): void {
        this.leave(this.future);
    }

    abort(): void {
        this.leave(null);
    }

    private leave(answer: RecurrenceFuture | null): void {
        if (this.choiceEvent && this.choiceEvent !== SingleChoice.EMPTY) {
            try {
                this.choiceEvent.choose(this.ticketId, "", "", "", 1);
            } catch {
                // nothing armed to tidy up
            }
        }
        this.router.navigate([`tickets/${this.ticketId}/edit`], {
            queryParams: answer ? { future: answer } : {},
        });
    }
}
